import { GraphQLClient } from 'graphql-request';
import SitemapQuery from 'globalQueries/Sitemap.query';
import SitemapXMLQuery from 'globalQueries/SitemapXML.query';
import FallbackSitemapQuery from 'globalQueries/FallbackSitemap.query';
import { requireTemplateByName } from 'lib/dependencies';
import { getHostname } from 'utils/http';
import getTemplateName from 'utils/template';
import { ContentTypes as allowedTemplates } from 'constants';

/**
 * Construct the api client for graphql request.
 */
const client = new GraphQLClient(process.env.WAGTAIL_API_URL);

if (process.env.WAGTAIL_API_TOKEN) {
  client.setHeader('Authorization', `Basic ${process.env.WAGTAIL_API_TOKEN}`);
}

export const buildPathFromSlug = ({ slug = [] }) => {
  let path = '/';
  if (slug.length > 0) {
    path += slug.join('/');
    if (process.env.__NEXT_TRAILING_SLASH) {
      path += '/';
    }
  }
  return path;
};

/**
 * The function will,
 *  1. Validate if the given url path also exist in the backend.
 *  2. If found, it will process the getCustomInitialProps from the template,
 *      so that they can be part of the actual props.
 *
 * @param {object} context nextjs context.
 */
export async function getPage({ preview, previewData, params = {}, req, query }, userLocation = null) {
  const currentUrl = buildPathFromSlug(params || {});
  // For local setup.
  // In the test/prd with will not have any effect
  const hostname = getHostname(req.headers.host);
  // eslint-disable-next-line no-console
  console.debug(`Wagtail CMS => Getting the page properties for '${hostname}' - '${currentUrl}'`);
  // Get all the pages
  let sitemap = await client.request(SitemapQuery, { hostname });

  if (!sitemap.site) {
    // eslint-disable-next-line no-console
    console.error(`No site is found for the site with hostname ${hostname}. Using fallback Query.`);
    sitemap = await client.request(FallbackSitemapQuery);
  }

  const sitemapPages = sitemap.site ? sitemap.site.pages : sitemap.pages || [];
  const footer = sitemap.site ? sitemap.site.footer : {};
  const menu = sitemap.site ? sitemap.site.mainmenu : {};
  const branding = sitemap.site && sitemap.site.sitebranding ? sitemap.site.sitebranding : null;
  const siteName = sitemap.site ? sitemap.site.siteName : '';
  const isDefaultSite = sitemap.site ? sitemap.site.isDefaultSite : false;
  const errorContent = sitemap.site && sitemap.site.errorContent ? sitemap.site.errorContent : {};
  const urgentarticle =
    sitemap.site && sitemap.site.urgentarticle ? sitemap.site.urgentarticle : { urgentArticle: null };
  const baseUrl = req.headers.referer || '';
  const sitesettings = sitemap.site && sitemap.site.sitesettings ? sitemap.site.sitesettings : { showWizard: true };
  const { cookiebar } = sitemap;
  const unusualopeninghours =
    sitemap.site && sitemap.site.unusualopeninghours ? sitemap.site.unusualopeninghours : { hideOpeningHours: false };
  const redirects = sitemap.redirects || {};

  const pageMeta = preview
    ? previewData
    : sitemapPages.find((page) => {
        /**
         * Check if url is an exact match (for single site)
         * Otherwise Check url with hostname prepended (Multi site)
         */
        if (!page.url) return false;
        if (page.url === currentUrl) return true;
        const pageFullUrl = page.url.replace(/^https?:\/\//, '');
        return `${hostname}${currentUrl}` === pageFullUrl;
      });

  if (typeof pageMeta === 'undefined') {
    // eslint-disable-next-line no-console
    console.error(`The path ${currentUrl} could not be found in the Wagtail sitemap response.`);
    return {
      errorContent,
      statusCode: 404,
      footer,
      branding,
      menu,
      siteName,
      urgentarticle,
      baseUrl,
      cookiebar,
      sitesettings,
      unusualopeninghours,
      redirects,
    };
  }

  const templateName = getTemplateName(pageMeta.contentType);
  if (!allowedTemplates.includes(templateName)) {
    // eslint-disable-next-line no-console
    console.error(`The template for '${templateName}' is not implemented.`);
    return {};
  }

  /**
   * Get the Page get parameter to determine the currentPage
   */
  let currentPage = 1;
  if (query.page) {
    const parsedPage = parseInt(query.page, 10);
    if (parsedPage > 0) {
      currentPage = parsedPage;
    }
  }

  /**
   * Get/Set the active theme from the query parameter.
   */
  let currentThemeId = 1;
  if (query.theme) {
    const parsedThemeId = parseInt(query.theme, 10);
    if (parsedThemeId > 0) {
      currentThemeId = parsedThemeId;
    }
  }

  let filterData = null;
  if (query.filters) {
    filterData = query.filters;
  }
  /**
   * Get/Set the active theme from the query parameter.
   */
  let filterThemeId = null;
  if (query.theme && query.theme !== 'null') {
    filterThemeId = query.theme;
  }

  // Found the page now passing the custom initial props.
  const Template = requireTemplateByName(templateName);

  try {
    const pageData = await Template.getCustomInitialProps({
      id: preview ? null : pageMeta.id,
      contentType: pageMeta.contentType,
      hostname,
      preview,
      previewData,
      currentPage,
      userLocation,
      currentThemeId,
      filterData,
      filterThemeId,
      unusualopeninghours,
      redirects,
    });

    // eslint-disable-next-line consistent-return
    return {
      pageMeta,
      ...pageData,
      footer,
      branding,
      menu,
      siteName,
      isDefaultSite,
      hostname,
      urgentarticle,
      baseUrl,
      cookiebar,
      sitesettings,
      redirects,
    };
  } catch (error) {
    throw new Error(`Failed to get getCustomInitialProps while fetching the page '${currentUrl}'`);
  }
}

/**
 * A simple wrapper to get individual page by provided ID.
 *
 *
 * @param {string} param0 query to execute (Can be passed from various templates)
 * @param {integer} param1 ID of the page
 */
export async function getPageOnHostByID({
  query,
  hostname,
  id = null,
  contentType = '',
  token = '',
  answers = [],
  lat,
  lon,
  currentPage = 1,
  themeId = 1,
  filters = [],
  filterThemeId = null,
}) {
  try {
    // eslint-disable-next-line no-console
    console.debug(`Wagtail CMS => get page on Host: '${hostname}' by ID: '${id}' and contentType: '${contentType}'`);
    const response = await client.request(query, {
      hostname,
      id,
      contentType,
      token,
      currentPage,
      answers,
      themeId,
      lat,
      lon,
      filters,
      filterThemeId,
    });
    return response;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(
      `Unable to retrieve pageData for ID: '${id}' and contentType: '${contentType}' on Host: '${hostname}'`,
    );
    return err;
  }
}

/**
 * Get the sitemap xml data.
 *
 */
export async function getSiteMap({ req }) {
  const hostname = getHostname(req.headers.host);

  try {
    // eslint-disable-next-line no-console
    console.debug(`Wagtail CMS => Getting the sitemap xml data for '${hostname}' `);
    const sitemapResponse = await client.request(SitemapXMLQuery, { hostname });
    return sitemapResponse;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(`Unable to retrieve sitemap xml data for '${hostname}'`);
    return err;
  }
}

/**
 * Perform a mutation for the given query with the payload on given host.
 *
 * @param {string} query  Graphql query to execute
 * @param {object} payload payload object.
 */
export async function performMutation(query, payload = {}) {
  try {
    const resp = await client.request(query, payload);
    return resp;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(`Failed to perform a mutation`);
    return err;
  }
}

/**
 * Fetch data for the given query with the payload. Mainly to handle few client side call.
 *
 * @param {string} query  Graphql query to execute
 * @param {object} payload payload object.
 */
export async function performQuery(query, payload = {}) {
  try {
    const resp = await client.request(query, payload);
    return resp;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(`Failed to perform a query`);
    return err;
  }
}
